import type * as React from 'react'
import { useState, useMemo } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'

import {
  AlertDialog,
  Button,
  CoachMark,
  NewBadge,
  toast,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { actions as profileActions } from '~publish/legacy/profiles/reducer'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import { useOptimizePostingSchedule } from '../../hooks/useOptimizePostingSchedule'

type ScheduleDay = {
  paused?: boolean
  times?: unknown[]
}

const SPOTLIGHT_STYLE: React.CSSProperties & Record<`--${string}`, string> = {
  '--spotlight-left': '0',
  '--spotlight-top': '5px',
}
const COACH_MARK_STYLE: React.CSSProperties & Record<`--${string}`, string> = {
  '--coach-mark-max-width': '360px',
}

export function MatchPostingTimesWithGoal(): JSX.Element {
  const dispatch = useDispatch()
  const channelData = useSelectedChannel()
  const {
    postingGoal,
    id: channelId,
    postingSchedule,
  } = channelData ?? {
    postingGoal: undefined,
    id: undefined,
    postingSchedule: undefined,
  }
  const [isLoadingRecommendedTimes, setIsLoadingRecommendedTimes] =
    useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [optimizePostingSchedule, { loading: isOptimizingPostingSchedule }] =
    useOptimizePostingSchedule()
  const goal = postingGoal?.goal ?? 0

  // Check if there are queued posts
  const { counts, loading: loadingCounts } = usePostCounts({
    organizationId: channelData?.organizationId,
    channelIds: channelId ? [channelId] : undefined,
  })
  const hasQueuedPosts = !loadingCounts && (counts?.queue ?? 0) > 0

  const activeSlotCount = useMemo(
    () => countActiveSlots(postingSchedule),
    [postingSchedule],
  )

  const buttonDisabled = goal === 0
  const buttonTooltip = buttonDisabled
    ? "You have not set a weekly Posting Goal for this channel. You can set the number of times you'd like to post each week above."
    : null
  const buttonShown = goal === 0 || (goal > 0 && goal > activeSlotCount)

  // Click handler for the "Add Posting Times to Match Goal" button
  // Show the confirmation modal if there are queued posts, otherwise add the recommended times
  const handleShowConfirmationModal = async (): Promise<void> => {
    if (hasQueuedPosts) {
      setShowConfirmModal(true)
      return Promise.resolve()
    } else {
      return handleAddRecommendedTimes()
    }
  }

  // Click handler for the "Confirm" button in the confirmation modal
  // or the "Add Posting Times to Match Goal" button if there are no queued posts
  const handleAddRecommendedTimes = async (): Promise<void> => {
    if (!channelId) {
      toast.error(
        'An error occurred while adding recommended posting times. Please try again later.',
      )
      return
    }
    // Wait 300ms before showing the loading state, if the request is fast, the loading state will not be shown
    const timer = setTimeout(() => setIsLoadingRecommendedTimes(true), 300)

    try {
      const { success, error } = await optimizePostingSchedule({
        variables: {
          input: {
            channelId,
            postsPerWeek: goal,
            updateMode: 'add',
          },
        },
      })

      clearTimeout(timer)
      dispatch(
        profileActions.fetchSingleProfile({
          profileId: channelId,
          message: '',
        }),
      )
      setIsLoadingRecommendedTimes(false)
      setShowConfirmModal(false)
      if (success) {
        toast.success('Posting times added successfully')
      } else {
        toast.error(
          `An error occurred while adding recommended posting times: ${error}`,
        )
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(
          `An error occurred while adding recommended posting times: ${error.message}`,
        )
      } else {
        toast.error(
          `An error occurred while adding recommended posting times. Please try again later.`,
        )
      }
      setIsLoadingRecommendedTimes(false)
    }
  }

  // Show the feature if the goal is greater than 0 and the active slot count is less than the goal
  // Show the confirmation modal if there are queued posts
  // or the "Add Posting Times to Match Goal" button if there are no queued posts
  if (!buttonShown) return <></>

  return (
    <>
      <AlertDialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <AlertDialog.Content>
          <AlertDialog.Title>Add Posting Slots to Match Goal</AlertDialog.Title>
          <AlertDialog.Description>
            You already have posts in your Queue. Updating your posting schedule
            will cause adjustments to the timing of these posts. We suggest
            checking your queue after making this change.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="primary"
                loading={isOptimizingPostingSchedule}
                onClick={handleAddRecommendedTimes}
              >
                Confirm
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
      {buttonDisabled ? (
        <Tooltip content={buttonTooltip}>
          <Button variant="secondary" disabled>
            Add Posting Slots to Match Goal
          </Button>
        </Tooltip>
      ) : (
        <FeatureCoachMark>
          <Button
            variant="secondary"
            onClick={handleShowConfirmationModal}
            loading={isLoadingRecommendedTimes || loadingCounts}
          >
            {isLoadingRecommendedTimes
              ? 'Finding Posting Slots'
              : 'Add Posting Slots to Match Goal'}
          </Button>
        </FeatureCoachMark>
      )}
    </>
  )
}

function FeatureCoachMark({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const banner = useDismissableBanner('match-posting-times-coachmark', {
    trackDismiss: true,
  })
  if (!banner.isActive) return <>{children}</>
  return (
    <CoachMark>
      <CoachMark.Overlay>
        {children}
        <CoachMark.Spotlight style={SPOTLIGHT_STYLE} />
      </CoachMark.Overlay>
      <CoachMark.Content side="left" style={COACH_MARK_STYLE}>
        <CoachMark.Title>
          <NewBadge /> Match Posting Slots With Your Goal
        </CoachMark.Title>
        <CoachMark.Description>
          We can add recommended times based on Buffer data to match your goal.
          Existing posting times and scheduled posts won&apos;t be affected.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="primary" size="small" onClick={banner.dismiss}>
              Got It
            </Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}

/**
 * Counts the number of active posting slots in a schedule
 *
 * @param schedule - Array of schedule days, where each day may have paused status and times
 * @returns The total number of active (non-paused) posting slots across all days
 *
 * @example
 * const schedule = [
 *   { paused: false, times: [1, 2] },
 *   { paused: true, times: [3, 4] }
 * ];
 * countActiveSlots(schedule); // Returns 2
 */
function countActiveSlots(schedule: ScheduleDay[] | undefined): number {
  if (!schedule) return 0
  return schedule.reduce((count: number, day: ScheduleDay) => {
    if (!day.paused && day.times) {
      return count + day.times.length
    }
    return count
  }, 0)
}
